import {
  ContentBlock,
  ContentState,
  Editor,
  EditorState,
  RichUtils,
} from "draft-js";
import { stateToHTML } from "draft-js-export-html";
import htmlToDraft from "html-to-draftjs";
import React, { FC, MouseEvent, useEffect, useRef, useState } from "react";

type Props = {
  value: string;
  onChange: (value: string) => void;
};

export const RichEditor: FC<Props> = ({ value, onChange }) => {
  const createEditorStateFromHTML = (html: string): EditorState => {
    if (!html) return EditorState.createEmpty();
    const contentBlock = htmlToDraft(html);
    if (!contentBlock) return EditorState.createEmpty();

    const contentState = ContentState.createFromBlockArray(
      contentBlock.contentBlocks,
      contentBlock.entityMap
    );

    return EditorState.createWithContent(contentState);
  };

  // État initial corrigé
  const [editorState, setEditorState] = useState(() =>
    createEditorStateFromHTML(value)
  );

  const editorRef = useRef<Editor | null>(null);

  // Mise à jour de l'éditeur quand `value` change
  useEffect(() => {
    const contentHTML = stateToHTML(editorState.getCurrentContent());
    if (value !== contentHTML) {
      setEditorState(createEditorStateFromHTML(value));
    }
  }, [value]);

  const focus = (): void => {
    editorRef.current?.focus();
  };

  const handleEditorChange = (newEditorState: EditorState): void => {
    setEditorState(newEditorState);
    const contentState = newEditorState.getCurrentContent();
    const html = stateToHTML(contentState);
    onChange(html);
  };

  const toggleInlineStyle = (inlineStyle: string): void => {
    handleEditorChange(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  };

  let className = "RichEditor-editor flex-1 font-nunito text-sm sm:text-base ";
  const contentState: ContentState = editorState.getCurrentContent();
  if (!contentState.hasText()) {
    if (contentState.getBlockMap().first()?.getType() !== "unstyled") {
      className += " RichEditor-hidePlaceholder";
    }
  }

  return (
    <div className="RichEditor-root flex-1">
      <InlineStyleControls
        editorState={editorState}
        onToggle={toggleInlineStyle}
      />
      <div className={className} onClick={focus}>
        {/* @ts-expect-error - Using ts-expect-error to bypass TypeScript errors with Editor component */}
        <Editor
          blockStyleFn={getBlockStyle}
          editorState={editorState}
          onChange={handleEditorChange}
          placeholder="Tell a story..."
          ref={editorRef}
          spellCheck={true}
        />
      </div>
    </div>
  );
};

function getBlockStyle(block: ContentBlock): string {
  switch (block.getType()) {
    case "blockquote":
      return "RichEditor-blockquote";
    default:
      return "";
  }
}

interface StyleButtonProps {
  onToggle: (style: string) => void;
  active: boolean;
  label: string;
  style: string;
}

const StyleButton: React.FC<StyleButtonProps> = ({
  onToggle,
  active,
  label,
  style,
}) => {
  const handleToggle = (e: MouseEvent): void => {
    e.preventDefault();
    onToggle(style);
  };

  let className =
    "RichEditor-styleButton px-2 sm:px-3 py-1 text-xs sm:text-sm rounded border";
  if (active) {
    className += " RichEditor-activeButton";
  }

  return (
    <span className={className} onMouseDown={handleToggle}>
      {label}
    </span>
  );
};

interface BlockType {
  label: string;
  style: string;
}

const INLINE_STYLES: BlockType[] = [
  { label: "Bold", style: "BOLD" },
  { label: "Italic", style: "ITALIC" },
  { label: "Underline", style: "UNDERLINE" },
];

interface InlineStyleControlsProps {
  editorState: EditorState;
  onToggle: (style: string) => void;
}

const InlineStyleControls: React.FC<InlineStyleControlsProps> = (props) => {
  const currentStyle = props.editorState.getCurrentInlineStyle();

  return (
    <div className="RichEditor-controls flex flex-wrap gap-1 sm:gap-2 mb-2 sm:mb-4">
      {INLINE_STYLES.map((type) => (
        <StyleButton
          key={type.label}
          active={currentStyle.has(type.style)}
          label={type.label}
          onToggle={props.onToggle}
          style={type.style}
        />
      ))}
    </div>
  );
};
